import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useCustomerSubuserAddressAccessAdmin,
  useUpdateCustomerSubuserAddressAccessAdmin,
  type CustomerSubuserAddressAccessItem,
} from '../../features/customers/api';
import { apiErrorMessage } from '../../utils/errors';

interface CustomerSubuserAddressAccessModalProps {
  customerId: string;
  subuser?: { id: string; name?: string | null; email?: string | null } | null;
  onClose: () => void;
}

export default function CustomerSubuserAddressAccessModal({
  customerId,
  subuser,
  onClose,
}: CustomerSubuserAddressAccessModalProps) {
  const { data, isLoading } = useCustomerSubuserAddressAccessAdmin(customerId, subuser?.id);
  const updateAccess = useUpdateCustomerSubuserAddressAccessAdmin(customerId, subuser?.id);

  const [rows, setRows] = useState<CustomerSubuserAddressAccessItem[]>([]);

  useEffect(() => {
    setRows(data?.addresses ?? []);
  }, [data]);

  const toggle = (id: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, isEnabled: !r.isEnabled } : r)));
  };

  const onSave = async () => {
    try {
      await updateAccess.mutateAsync({
        addresses: rows.map((r) => ({
          deliveryAddressId: r.id,
          isEnabled: !!r.isEnabled,
        })),
      });
      toast.success('Subuser address access updated.');
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Save failed.'));
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel" style={{ maxWidth: 760, width: '100%' }}>
        <div className="modal__header">
          <div>
            <h3 className="modal__title">Manage Subuser Addresses</h3>
            <div className="muted" style={{ marginTop: 4 }}>
              {subuser?.name || subuser?.email}
            </div>
          </div>

          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="modal__body">
          {isLoading ? (
            <div className="skeleton" style={{ height: 180 }} />
          ) : rows.length === 0 ? (
            <div className="muted">No customer addresses found.</div>
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {rows.map((r) => (
                <label
                  key={r.id}
                  className="card"
                  style={{
                    padding: 12,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!r.isEnabled}
                    onChange={() => toggle(r.id)}
                    style={{ marginTop: 4 }}
                  />

                  <div>
                    <div style={{ fontWeight: 600 }}>{r.label || 'Address'}</div>
                    <div className="muted tiny" style={{ marginTop: 4 }}>
                      {[r.addressLine1, r.addressLine2].filter(Boolean).join(', ')}
                      {([r.addressLine1, r.addressLine2].some(Boolean) &&
                      (r.city || r.state || r.postalCode)
                        ? ', '
                        : '') + [r.city, r.state, r.postalCode].filter(Boolean).join(', ')}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="actions" style={{ marginTop: 14, gap: 8, justifyContent: 'flex-end' }}>
            <button
              className="btn btn--primary"
              onClick={onSave}
              disabled={isLoading || updateAccess.isPending}
            >
              {updateAccess.isPending ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
